import type { VFrameBase } from '@festina/legalentity-general-types/legalentity'
import type { Simulation } from '@festina/life-general-types/life'
import {
  type ActionIdRegistry,
  type Except,
  type ExcludeString,
  type Optional,
  type Server,
  stringUtils,
} from '@festina-life/core'
import { Button, ButtonContainer } from '@festina-life/ui/actions'
import { Alert, LinearSpinner, Tooltip } from '@festina-life/ui/feedback'
import {
  FormikChangesButton,
  type SubmitStatus,
} from '@festina-life/ui/formikForms'
import {
  FixedWidthInputs,
  ShowChangesButtonProvider,
} from '@festina-life/ui/forms'
import { useTranslate } from '@festina-life/ui/hooks'
import { spacingStyles } from '@festina-life/ui/libs'
import { JsTreeView } from '@festina-life/ui/listsAndTables'
import { Modal, Stack } from '@festina-life/ui/structure'
import {
  type ReactNode,
  useCallback,
  useId,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import { ErrorBoundary } from 'react-error-boundary'

import {
  useScenarioContext,
  type VariantIdentifier,
} from '../../context/scenarioContext'
import { useComponent } from '../../lenteHooks'
import { ErrorMessage } from '../errorFallback/ErrorMessage'
import { LifeErrorFallback } from '../errorFallback/LifeErrorFallback'
import { scenarioStyles } from '../participantOverview/scenarios/scenarioStyles'
import { LifeCommitmentSaveOrCreateScenarioModalButton } from '../scenario/LifeCommitmentSaveOrCreateScenarioModalButton'
import { useActionsContext } from './actionsContext'
import { AddToScenarioLabel } from './AddToScenarioLabel'
import {
  CommandDataFormAndSimulation,
  type CommandDataFormProps,
  type CommandFormAndSimulationProps,
} from './CommandDataFormAndSimulation'
import { shouldShowSimulationAndAllowFetchForFrame } from './CommandDataFormAndSimulation.utils'
import { CommandModalButtonActions } from './CommandModalButtonActions'
import { CommandModalButtonRegister } from './CommandModalButtonRegister'
import { CommandModalWarnings } from './CommandModalWarnings'
import { FormValidationSwitch } from './FormValidationSwitch'
import type { AnyCommandVersion } from './types'

type CommandDataFormComponent<
  T extends NonNullable<AnyCommandVersion['commandData']>,
  E,
  F extends VFrameBase = VFrameBase,
> = (props: CommandDataFormProps<T, E, F>) => ReactNode

type CommandFormSubmitFn = () => Promise<unknown>

type CommandFormSubmitHandle = {
  submitForm: CommandFormSubmitFn
}

type Props<
  T extends NonNullable<AnyCommandVersion['commandData']>,
  S extends Simulation = Simulation,
  E = string,
  F extends VFrameBase = VFrameBase,
> = {
  actionId: Extract<
    keyof ActionIdRegistry,
    `${string}.${NoInfer<ExcludeString<Extract<T['_type'], string>>>}`
  >
  title: string
  /**
   * Will be shown in the modal title after the `title`. Used for extra context like which entityId the command is being executed on.
   */
  modalTitleExtra?: string
  buttonVariant?: 'primary' | 'secondary' | 'discreet'
  isFutureCommand?: boolean
  commandId?: Optional<string>
  festinaComponent: Server
  variantIdentifierOverwrite?: VariantIdentifier
  /** Frame fetcher service method obtained from the command actions. */
  // Optional for now until more commands are converted to lazy and we can finally make the switch.
  fetchFrame?: (commandData: T) => Promise<F | null>
  extraRegistrationKey?: string
} & Except<
  CommandFormAndSimulationProps<NoInfer<T>, S, E, F>,
  | 'onStatusChanged'
  | 'fetchFrame'
  | 'onFrameIsLoadingChanged'
  | 'commandDataForm'
  | 'onSetSubmitForm'
> & {
    commandDataForm: CommandDataFormComponent<T, E, F>
  }

export const CommandModalButton = <
  T extends NonNullable<AnyCommandVersion['commandData']>,
  S extends Simulation = Simulation,
  E = string,
>({
  actionId,
  entityId,
  title,
  modalTitleExtra,
  simulation,
  submit,
  commandActionVariant,
  commandId,
  buttonVariant = 'secondary',
  eventVersionWithCommand,
  festinaComponent,
  isFutureCommand = false,
  isAllowedToDoCalculation = true,
  extraRegistrationKey,
  ...props
}: Props<T, S, E>) => {
  const { currentId: openActionId, onClose } = useActionsContext()
  const [status, setStatus] = useState<SubmitStatus>()
  const [isFormWithValidation, setIsFormWithValidation] = useState(false)
  const [isValidationDisabled, setIsValidationDisabled] = useState(false)
  const [isShowingChanges, setIsShowingChanges] = useState(false)
  const [isFrameLoading, setIsFrameLoading] = useState(false)
  const [hasUpdateError, setHasUpdateError] = useState(false)
  const translate = useTranslate()
  const { variantIdentifier } = useScenarioContext()

  const { commandVersion } = eventVersionWithCommand ?? {}

  const registrationId =
    actionId +
    commandActionVariant +
    commandId +
    (entityId ? JSON.stringify(entityId) : '') +
    eventVersionWithCommand?.rolledBackTime +
    isFutureCommand +
    (extraRegistrationKey ?? '')

  const isModalOpen = openActionId === registrationId
  const isActionAvailable = useMemo(() => {
    if (submit?.authorization.isUserAllowedToDoAction === undefined) {
      return true
    } else {
      return commandActionVariant === 'read'
        ? true
        : !!submit?.authorization.isUserAllowedToDoAction
    }
  }, [submit?.authorization.isUserAllowedToDoAction, commandActionVariant])

  const canUserDisableClientValidation = !!isFormWithValidation

  const formId = useId()
  const commandFormSubmitFnRef = useRef<CommandFormSubmitFn | undefined>(
    undefined,
  )
  const commandFormSubmitHandleRef = useRef<CommandFormSubmitHandle>(null)

  useImperativeHandle(
    commandFormSubmitHandleRef,
    () => ({
      submitForm: async () => {
        await commandFormSubmitFnRef.current?.()
      },
    }),
    [],
  )

  const handleSetCommandFormSubmit = useCallback(
    (submitForm: CommandFormSubmitFn | undefined) => {
      commandFormSubmitFnRef.current = submitForm
    },
    [],
  )

  const handleSubmitFromScenarioManager = useCallback(async () => {
    await commandFormSubmitHandleRef.current?.submitForm()
  }, [])

  const handleClose = useCallback(() => {
    setIsValidationDisabled(false)
    setIsFrameLoading(false)
    setHasUpdateError(false)
    onClose()
    // Doing it after onClose will still show the changes briefly while closing the modal
    // Therefor a small timeout is being done
    // LIFE-8249: To get rid of the timeout
    setTimeout(() => setIsShowingChanges(false), 1000)
  }, [onClose])

  const handleStatusChanged = (status: SubmitStatus) => {
    setStatus(status)

    if (status === 'success') {
      handleClose()
    }
    if (status === 'submitting') {
      setIsShowingChanges(false)
    }
  }

  const primaryButtonText: Record<
    Exclude<typeof commandActionVariant, 'read' | 'simulationOnly'>,
    string
  > = {
    create: translate('apply'),
    update: translate('update'),
    approve: translate('approve'),
    cancel: translate('delete'),
    approvePendingCancellation: translate('approvePendingCancellation'),
    declinePendingCancellation: translate('declinePendingCancellation'),
  }

  const collectionCommitmentActionButtons = useComponent(
    'pages.commandDataActions.life.collectionCommitmentActionButtons',
  )
  const collectionPersonPretendCommandPaletteButtons = useComponent(
    'pages.commandDataActions.life.collectionPersonPretendCommandPaletteButtons',
  )

  const commandType = actionId.split('.').at(1) ?? ''
  const shouldShowScenariosButton =
    commandType in collectionCommitmentActionButtons ||
    commandType in collectionPersonPretendCommandPaletteButtons

  const allowedToShowSimulationAndAllowFetchForFrame =
    shouldShowSimulationAndAllowFetchForFrame({
      commandActionVariant,
      isAllowedToDoCalculation,
      commandVersion,
    })

  return (
    <>
      <CommandModalButtonRegister
        actionId={actionId}
        title={title}
        registrationId={registrationId}
        entityId={entityId}
        isActionAvailable={isActionAvailable}
        commandActionVariant={commandActionVariant}
      />
      {(commandActionVariant === 'read' || submit?.onSubmit) &&
        commandActionVariant !== 'simulationOnly' && (
          <CommandModalButtonActions
            eventVersionWithCommand={eventVersionWithCommand}
            buttonText={title}
            isActionAvailable={isActionAvailable}
            registrationId={registrationId}
            commandActionVariant={commandActionVariant}
            buttonVariant={buttonVariant}
            isFutureCommand={isFutureCommand}
          />
        )}
      <Modal
        isOpen={isModalOpen}
        panelClassName="min-h-56" // Previously we increased the min-height of the modal when there was a simulation, but that never worked so for now we just set a min-height to ensure that command modals are not too small e.g. when loading too.
        onClose={handleClose}
        style={variantIdentifier ? scenarioStyles : undefined}
      >
        <ShowChangesButtonProvider>
          <Modal.Title
            className="relative" // relative is needed for the spinner to be positioned correctly
            title={
              <>
                {modalTitleExtra ? `${title}, ${modalTitleExtra}` : title}
                {isFrameLoading && (
                  <LinearSpinner className="absolute! left-0 bottom-0 right-0 translate-y-1/2 bg-transparent!" />
                )}
              </>
            }
          />
          <Modal.Body innerClassName={spacingStyles({ gap: 'spacious' })}>
            <FixedWidthInputs>
              {commandActionVariant === 'create' && (
                <CommandModalWarnings actionId={actionId} entityId={entityId} />
              )}
              <ErrorBoundary
                onError={() => {
                  if (commandActionVariant === 'update') {
                    setHasUpdateError(true)
                  }
                }}
                FallbackComponent={(fallbackProps) =>
                  commandActionVariant === 'create' ? (
                    <LifeErrorFallback {...fallbackProps} />
                  ) : (
                    <>
                      {commandActionVariant === 'update' ? (
                        <>
                          <Alert
                            severity="error"
                            title={translate(
                              'commandModalButton.unableToEditToDoCriticalError.title',
                            )}
                          >
                            {translate(
                              'commandModalButton.unableToEditToDoCriticalError.body',
                            )}
                          </Alert>
                          <ErrorMessage error={fallbackProps.error} />
                        </>
                      ) : (
                        <>
                          <Tooltip
                            tip={<ErrorMessage error={fallbackProps.error} />}
                            isInteractive
                          >
                            <Alert
                              severity="error"
                              title={translate(
                                'commandModalButton.viewError.title',
                              )}
                            >
                              <Stack className="flex-col" gap="condensed">
                                <div>
                                  {translate(
                                    'commandModalButton.viewError.body',
                                  )}
                                </div>
                                <div>
                                  {translate(
                                    'commandModalButton.hoverAlertToSeeErrorDetails',
                                  )}
                                </div>
                              </Stack>
                            </Alert>
                          </Tooltip>
                          <form
                            id={formId}
                            onSubmit={async () =>
                              // The way our CommandData works, we only get here for when it is not create or update.
                              // The parameters are automatically gotten for these, and therefor it only need to fire off the request.
                              await submit?.onSubmit({
                                _type: 'FailureBackup',
                              } as T)
                            }
                          >
                            <JsTreeView data={commandVersion?.commandData} />
                          </form>
                        </>
                      )}
                    </>
                  )
                }
              >
                <CommandDataFormAndSimulation<T, S, E>
                  submit={submit}
                  formId={formId}
                  simulation={simulation}
                  onStatusChanged={handleStatusChanged}
                  commandActionVariant={commandActionVariant}
                  eventVersionWithCommand={eventVersionWithCommand}
                  entityId={entityId}
                  isValidationDisabled={isValidationDisabled}
                  onFormHasValidationChanged={setIsFormWithValidation}
                  shouldShowChangesInForm={isShowingChanges}
                  festinaComponent={festinaComponent}
                  onFrameIsLoadingChanged={setIsFrameLoading}
                  onSetSubmitForm={handleSetCommandFormSubmit}
                  isAllowedToDoCalculation={
                    allowedToShowSimulationAndAllowFetchForFrame
                  }
                  {...props}
                />
              </ErrorBoundary>
            </FixedWidthInputs>
          </Modal.Body>
          <Modal.Footer>
            <ButtonContainer className="grow">
              {canUserDisableClientValidation && (
                <FormValidationSwitch
                  isValidationDisabled={isValidationDisabled}
                  onValidationDisabledChange={setIsValidationDisabled}
                />
              )}
              {(commandActionVariant === 'update' ||
                commandActionVariant === 'create') && (
                <FormikChangesButton
                  isShowingChanges={isShowingChanges}
                  onClick={setIsShowingChanges}
                />
              )}
            </ButtonContainer>
            <Button onClick={handleClose}>{translate('cancel')}</Button>
            {shouldShowScenariosButton &&
              festinaComponent === 'life' &&
              commandActionVariant === 'create' && (
                <LifeCommitmentSaveOrCreateScenarioModalButton
                  actionId={actionId}
                  onSubmitScenarioSuccess={handleSubmitFromScenarioManager}
                />
              )}

            {!stringUtils.strictIncludes(commandActionVariant, [
              'read',
              'simulationOnly',
            ]) &&
              !hasUpdateError &&
              submit && (
                <Button
                  variant="primary"
                  isDanger={
                    commandActionVariant === 'cancel' ||
                    commandActionVariant === 'declinePendingCancellation' ||
                    commandActionVariant === 'approvePendingCancellation'
                  }
                  isLoading={status === 'submitting'}
                  type="submit"
                  form={formId}
                  // When we submit, we always want to go over to the normal view.
                  // Adding an onClick here always ensures it.
                  onClick={() =>
                    isShowingChanges ? setIsShowingChanges(false) : undefined
                  }
                >
                  {variantIdentifier && commandActionVariant === 'create' ? (
                    <AddToScenarioLabel />
                  ) : (
                    primaryButtonText[commandActionVariant]
                  )}
                </Button>
              )}
          </Modal.Footer>
        </ShowChangesButtonProvider>
      </Modal>
    </>
  )
}
